import React, { useMemo, useState, useEffect, useRef } from 'react';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './actions-dropdown.module.scss';
import { Dropdown } from 'src/components/design-system';
import { Tooltip } from 'src/components/core';

function FocusableTooltip({ title, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!title) return;
    const el = ref.current;
    const menuItem = el?.closest('[role="menuitem"]');
    if (!menuItem) return;

    const show = () => setOpen(true);
    const hide = () => setOpen(false);

    menuItem.addEventListener('focus', show);
    menuItem.addEventListener('blur', hide);
    menuItem.addEventListener('mouseenter', show);
    menuItem.addEventListener('mouseleave', hide);
    return () => {
      menuItem.removeEventListener('focus', show);
      menuItem.removeEventListener('blur', hide);
      menuItem.removeEventListener('mouseenter', show);
      menuItem.removeEventListener('mouseleave', hide);
    };
  }, [title]);

  return (
    <Tooltip title={title} open={title ? open : undefined}>
      <span ref={ref}>{children}</span>
    </Tooltip>
  );
}

/**
 * ActionsDropdown component
 *
 * @param {{
 * actions: {
 *  label: string;
 *  onClick: () => void;
 *  props?: React.HTMLProps<HTMLDivElement>;
 * }[]
 * }} param
 *
 */
export function ActionsDropdown({ actions }) {
  const items = useMemo(
    () =>
      actions.map((action, index) => {
        const hasTooltipWhenDisabled = action.disabled && action.tooltip;

        return {
          key: String(index),
          hide: action.props?.hidden,
          label: (
            <FocusableTooltip title={action.tooltip}>
              <span
                {...action.props}
                style={
                  hasTooltipWhenDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined
                }>
                {action.label}
              </span>
            </FocusableTooltip>
          ),
          disabled: hasTooltipWhenDisabled ? false : action.disabled,
          onClick: e => {
            e.domEvent.stopPropagation();
            e.domEvent.preventDefault();
            if (action.disabled) return;
            action.onClick(e.domEvent);
          },
        };
      }),
    [actions],
  );

  return (
    <Tooltip title="Edit actions">
      <Dropdown
        items={items}
        aria-label="Edit actions"
        trigger={['click']}
        childrenWrapperStyle={styles.toggleButton}>
        <FontAwesomeIcon icon={faPencil} className={styles.actionsIcon} />
      </Dropdown>
    </Tooltip>
  );
}
